"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.facebookAdsCampaignManagement = exports.facebookAdsCampaignManagementArgsSchema = exports.FacebookAdsCampaignAction = exports.facebookAdsUpdateCampaignResultSchema = exports.facebookAdsUpdateCampaignArgsSchema = exports.facebookAdsCreateCampaignResultSchema = exports.facebookAdsCreateCampaignArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// ---------- Create Campaign ----------
exports.facebookAdsCreateCampaignArgsSchema = zod_1.z.object({
    name: zod_1.z.string().describe('Campaign name'),
    objective: zod_1.z
        .enum([
        'OUTCOME_APP_PROMOTION',
        'OUTCOME_AWARENESS',
        'OUTCOME_ENGAGEMENT',
        'OUTCOME_LEADS',
        'OUTCOME_SALES',
        'OUTCOME_TRAFFIC',
        'PAGE_LIKES',
    ])
        .describe('Campaign objective. Use OUTCOME_TRAFFIC for video ads to optimize for video views and engagement.'),
    status: zod_1.z
        .enum(['ACTIVE', 'PAUSED', 'DELETED', 'ARCHIVED'])
        .default('PAUSED')
        .describe('Campaign status (default: PAUSED)'),
    specialAdCategories: zod_1.z
        .array(zod_1.z.enum(['NONE', 'EMPLOYMENT', 'HOUSING', 'CREDIT', 'ISSUES_ELECTIONS_POLITICS']))
        .default([])
        .nullable()
        .optional()
        .transform(function (categories) {
        // If NONE is present, return empty array as Meta API expects
        if (categories === null || categories === void 0 ? void 0 : categories.includes('NONE')) {
            return [];
        }
        return categories || [];
    })
        .describe('Special ad categories if applicable. For campaigns with no special ad category requirements, this will be forced to an empty array [] as required by Meta API'),
    dailyBudget: zod_1.z
        .number()
        .nullish()
        .describe('Daily budget in account currency (cents). Minimum $1.00 (100 cents).'),
    lifetimeBudget: zod_1.z
        .number()
        .nullish()
        .describe('Lifetime budget in account currency (cents). Minimum $1.00 (100 cents).'),
    bidStrategy: zod_1.z
        .enum([
        'LOWEST_COST_WITHOUT_CAP',
        'LOWEST_COST_WITH_BID_CAP',
        'COST_CAP',
        'LOWEST_COST_WITH_MIN_ROAS',
    ])
        .nullish()
        .describe('Bid strategy for the campaign (applies to all ad sets in CBO campaigns). LOWEST_COST_WITHOUT_CAP (default): automatic bidding, LOWEST_COST_WITH_BID_CAP: maximum bid per auction, COST_CAP: target average cost, LOWEST_COST_WITH_MIN_ROAS: minimum ROAS constraint. Note: bid amounts are set at ad set level, not campaign level.'),
    bidConstraints: zod_1.z
        .object({
        roas_average_floor: zod_1.z
            .number()
            .nullable()
            .optional()
            .describe('Minimum ROAS target scaled by 10,000. Required when optimization goal is VALUE. Examples: 1.5 ROAS = 15000, 2.0 ROAS = 20000, 3.0 ROAS = 30000. Meta API requires values multiplied by 10,000.'),
    })
        .nullable()
        .optional()
        .describe('Bid constraints for ROAS optimization in CBO campaigns. Use with optimization goal VALUE.'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Campaign start date in YYYY-MM-DD format. Required for budget scheduling.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('Campaign end date in YYYY-MM-DD format. Required for lifetime budgets.'),
    adsetBudgetSharing: zod_1.z
        .boolean()
        .nullish()
        .describe('Enable ad set budget sharing for ABO campaigns (when campaign has no budget). When true, ad sets can share up to 20% of their budget to optimize overall performance. Only applies to campaigns without campaign-level budgets (ABO mode). Defaults to false if not specified. Not applicable for CBO campaigns (campaigns with dailyBudget or lifetimeBudget).'),
});
exports.facebookAdsCreateCampaignResultSchema = zod_1.z.object({
    id: zod_1.z.string().describe('Campaign ID'),
    name: zod_1.z.string(),
    status: zod_1.z.string(),
    created: zod_1.z.boolean().default(true),
    adAccountId: zod_1.z.string().nullish().describe('Ad account ID used to create the campaign'),
});
// ---------- Update Campaign ----------
exports.facebookAdsUpdateCampaignArgsSchema = zod_1.z.object({
    campaignId: zod_1.z.string().describe('Campaign ID to update'),
    name: zod_1.z.string().nullish().describe('New campaign name'),
    status: zod_1.z.enum(['ACTIVE', 'PAUSED', 'DELETED']).nullish().describe('New campaign status'),
    dailyBudget: zod_1.z
        .number()
        .nullish()
        .describe('New daily budget in account currency (cents). Minimum $1.00 (100 cents).'),
    lifetimeBudget: zod_1.z
        .number()
        .nullish()
        .describe('New lifetime budget in account currency (cents). Minimum $1.00 (100 cents).'),
    bidStrategy: zod_1.z
        .enum([
        'LOWEST_COST_WITHOUT_CAP',
        'LOWEST_COST_WITH_BID_CAP',
        'COST_CAP',
        'LOWEST_COST_WITH_MIN_ROAS',
    ])
        .nullish()
        .describe('New bid strategy for the campaign (applies to all ad sets in CBO campaigns). LOWEST_COST_WITHOUT_CAP: automatic bidding, LOWEST_COST_WITH_BID_CAP: maximum bid per auction, COST_CAP: target average cost, LOWEST_COST_WITH_MIN_ROAS: minimum ROAS constraint. Note: bid amounts are set at ad set level, not campaign level.'),
    bidConstraints: zod_1.z
        .object({
        roas_average_floor: zod_1.z
            .number()
            .nullable()
            .optional()
            .describe('Minimum ROAS target scaled by 10,000. Required when optimization goal is VALUE. Examples: 1.5 ROAS = 15000, 2.0 ROAS = 20000, 3.0 ROAS = 30000. Meta API requires values multiplied by 10,000.'),
    })
        .nullable()
        .optional()
        .describe('New bid constraints for ROAS optimization in CBO campaigns. Use with optimization goal VALUE.'),
    startDate: zod_1.z.string().nullish().describe('New campaign start date in YYYY-MM-DD format.'),
    endDate: zod_1.z.string().nullish().describe('New campaign end date in YYYY-MM-DD format.'),
});
exports.facebookAdsUpdateCampaignResultSchema = zod_1.z.object({
    id: zod_1.z.string().describe('Campaign ID'),
    updated: zod_1.z.boolean().default(true),
    success: zod_1.z.boolean(),
    adAccountId: zod_1.z.string().nullish().describe('Ad account ID used to update the campaign'),
});
// ==========================
// Compound Tool: facebookAdsCampaignManagement
// ==========================
exports.FacebookAdsCampaignAction = zod_1.z.enum(['createCampaign', 'updateCampaign']);
exports.facebookAdsCampaignManagementArgsSchema = zod_1.z.object({
    action: zod_1.z.discriminatedUnion('name', [
        zod_1.z.object({ name: zod_1.z.literal('createCampaign'), params: exports.facebookAdsCreateCampaignArgsSchema }),
        zod_1.z.object({ name: zod_1.z.literal('updateCampaign'), params: exports.facebookAdsUpdateCampaignArgsSchema }),
    ]),
    emitMode: zod_1.z.literal('single'),
});
var FacebookAdsCampaignManagementAllResultsSchema = zod_1.z.union([
    exports.facebookAdsCreateCampaignResultSchema,
    exports.facebookAdsUpdateCampaignResultSchema,
]);
var facebookAdsCampaignManagementResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action.name'],
    variants: [
        { when: { 'action.name': 'createCampaign' }, schema: exports.facebookAdsCreateCampaignResultSchema },
        { when: { 'action.name': 'updateCampaign' }, schema: exports.facebookAdsUpdateCampaignResultSchema },
    ],
    defaultSchema: FacebookAdsCampaignManagementAllResultsSchema,
});
exports.facebookAdsCampaignManagement = (0, common_1.createToolMetadata)({
    id: 'facebookAdsCampaignManagement',
    displayName: 'Facebook Campaign Management',
    displayDescription: 'Create and update Facebook Ads campaigns',
    category: 'ads',
    color: common_1.BRAND_COLORS.META,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.META),
    icon: 'meta',
    argsSchema: exports.facebookAdsCampaignManagementArgsSchema,
}, facebookAdsCampaignManagementResultSchemas);
